import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ShieldCheck, Files, Gavel, KanbanSquare, Building2, ArrowRight, Lock, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";

const FEATURES = [
  { icon: Files, title: "Multi-Bureau Parsing", body: "Equifax, Experian, TransUnion and any monitoring service — parsed into clean, reviewable data." },
  { icon: Gavel, title: "FCRA & FDCPA Templates", body: "Bureau, furnisher, debt validation, MOV, follow-up and arbitration letters — ready to sign." },
  { icon: Building2, title: "CFPB Complaint Builder", body: "A guided wizard drafts a complete complaint narrative, timeline, and requested resolution." },
  { icon: KanbanSquare, title: "Dispute Tracker", body: "See every letter as it moves from Drafted → Sent → Response → Resolved." },
];

export default function Landing() {
  const nav = useNavigate();
  const [disclaimer, setDisclaimer] = useState("");

  useEffect(() => {
    api.get("/settings/public").then((r) => setDisclaimer(r.data.disclaimer)).catch(() => {});
  }, []);

  return (
    <div className="relative min-h-screen grain">
      {/* Nav */}
      <header className="relative z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-purple-400 to-purple-600 grid place-items-center">
              <ShieldCheck size={18} className="text-white" />
            </div>
            <span className="font-semibold tracking-tight text-slate-900">Informed <span className="text-purple-600">Accelerator</span></span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/login" data-testid="landing-signin-link" className="text-sm text-slate-700 hover:text-slate-900 px-3 py-2">Sign in</Link>
            <Button data-testid="landing-start-dispute-button" className="btn-primary rounded-xl px-4" onClick={() => nav("/signup")}>
              Start My Dispute <ArrowRight size={16} className="ml-1" />
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-16 lg:pt-24 lg:pb-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="eyebrow mb-5">Skool Community · Executive Grade</div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight leading-[1.05]">
              Turn Your Credit Report<br />
              Into an <span className="text-purple-600">Action Plan.</span>
            </h1>
            <p className="mt-6 text-slate-700 text-lg max-w-2xl leading-relaxed">
              Upload your credit report, review the accounts that may need attention, and create personalized bureau letters and CFPB complaint drafts — all from one simple dashboard.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button data-testid="landing-hero-cta-button" size="lg" className="btn-primary rounded-xl px-6 py-6 text-base" onClick={() => nav("/signup")}>
                Start My Dispute <ArrowRight size={16} className="ml-1" />
              </Button>
              <Link to="/login" data-testid="landing-hero-signin-link" className="text-sm text-slate-700 hover:text-slate-900 px-4 py-3">
                Already have an account? Sign in
              </Link>
            </div>
            <div className="mt-8 flex items-center gap-4 text-sm text-slate-500">
              <span className="flex items-center gap-1.5"><Lock size={14} className="text-purple-600" /> Encrypted, private storage</span>
              <span className="flex items-center gap-1.5"><Sparkles size={14} className="text-purple-600" /> AI-assisted, human-controlled</span>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-6 bg-purple-500/10 blur-3xl rounded-3xl" />
              <Card className="relative glass rounded-2xl overflow-hidden hover-lift purple-glow">
                <CardContent className="p-0">
                  <div className="p-6 border-b border-slate-200 flex items-center justify-between">
                    <div>
                      <div className="eyebrow">Preview</div>
                      <div className="mt-1 text-slate-900 font-semibold">Dispute Command Center</div>
                    </div>
                    <span className="stage-pill rounded-full px-3 py-1 text-xs">Live</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 p-6">
                    {["Collections","Charge-Offs","Late Payments","Positive"].map((t,i)=>(
                      <div key={t} className="rounded-xl bg-slate-50/70 border border-slate-200 p-4">
                        <div className="text-xs text-slate-500">{t}</div>
                        <div className="mt-2 text-2xl font-semibold text-slate-900">{[2,1,1,1][i]}</div>
                      </div>
                    ))}
                  </div>
                  <div className="p-6 border-t border-slate-200 space-y-2">
                    {["Midland Credit Management","Portfolio Recovery","Capital One (Charge-Off)"].map(n=>(
                      <div key={n} className="flex items-center justify-between rounded-lg bg-slate-50/50 border border-slate-200 px-3 py-2 text-sm">
                        <span className="text-slate-800">{n}</span>
                        <span className="badge-danger rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest">Negative</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="relative z-10 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="eyebrow">Built for outcomes</div>
          <h2 className="mt-2 text-2xl sm:text-3xl lg:text-4xl font-semibold text-slate-900">Everything you need to run a professional dispute.</h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {FEATURES.map(({icon:Icon, title, body}) => (
              <Card key={title} className="glass rounded-2xl hover-lift">
                <CardContent className="p-6">
                  <div className="h-10 w-10 rounded-xl bg-purple-500/15 text-purple-700 grid place-items-center border border-purple-400/20">
                    <Icon size={18} />
                  </div>
                  <h3 className="mt-4 text-lg font-semibold text-slate-900">{title}</h3>
                  <p className="mt-2 text-sm text-slate-500 leading-relaxed">{body}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Flow */}
      <section className="relative z-10 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid lg:grid-cols-5 gap-4">
            {["Upload","Review","Select","Generate","Download"].map((s,i)=>(
              <div key={s} className="glass rounded-2xl p-6">
                <div className="eyebrow">Step {i+1}</div>
                <div className="mt-2 text-xl font-semibold text-slate-900">{s}</div>
                <div className="mt-2 h-1 rounded-full bg-slate-50 overflow-hidden">
                  <div className="h-full bg-purple-400" style={{width: `${(i+1)*20}%`}} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="relative z-10 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="glass rounded-2xl p-6">
            <div className="eyebrow">Disclaimer</div>
            <p className="mt-2 text-sm text-slate-500 leading-relaxed" data-testid="landing-disclaimer">
              {disclaimer || "This application provides educational tools and document-drafting assistance. Results are not guaranteed, and the information provided does not constitute legal advice."}
            </p>
            <p className="mt-3 text-xs text-slate-400">Access to this tool is provided through the Informed Accelerator community.</p>
          </div>
        </div>
      </section>

      <footer className="relative z-10 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-sm text-slate-400 flex flex-wrap justify-between gap-2">
          <span>© {new Date().getFullYear()} Informed Accelerator</span>
          <span>Skool Community MVP · v0.1</span>
        </div>
      </footer>
    </div>
  );
}
